import { useState, useCallback } from 'react';

/**
 * useConfirm — shared hook that replaces window.confirm with a MUI dialog.
 *
 * Returns:
 *   confirm(options)  — call to request confirmation.
 *                       options: { title?, message, confirmLabel?, cancelLabel?, destructive?, onConfirm }
 *   confirmState      — pass this to <ConfirmDialog {...confirmState} />
 *   closeConfirm      — closes the dialog (cancel / backdrop)
 *
 * Usage:
 *   const { confirm, confirmState, closeConfirm } = useConfirm();
 *   ...
 *   confirm({ message: 'Delete this?', destructive: true, onConfirm: () => doDelete(id) });
 *   ...
 *   <ConfirmDialog {...confirmState} onClose={closeConfirm} />
 */
export default function useConfirm() {
  const [confirmState, setConfirmState] = useState({ open: false });

  const closeConfirm = useCallback(() => {
    setConfirmState({ open: false });
  }, []);

  const confirm = useCallback(({
    title = 'Confirm',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
  }) => {
    setConfirmState({
      open: true,
      title,
      message,
      confirmLabel,
      cancelLabel,
      destructive,
      onConfirm: () => {
        setConfirmState({ open: false });
        if (onConfirm) onConfirm();
      },
    });
  }, []);

  return { confirm, confirmState, closeConfirm };
}